import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Upload, Shield, AlertCircle } from 'lucide-react';
import { colors } from '../../constants/theme';
import {
  getEligibleCategories,
  calculateAge,
  CATEGORY_LABELS,
} from '../../constants/categories';
import { registrationsService } from '../../services/api';

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIMETYPES = ['image/png', 'image/jpeg', 'application/pdf'];

export default function RegistrationPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [phone, setPhone] = useState('');
  const [category, setCategory] = useState('');
  const [paymentProof, setPaymentProof] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const eligibleCategories = useMemo(
    () => (birthDate ? getEligibleCategories(birthDate) : []),
    [birthDate],
  );

  const age = useMemo(() => calculateAge(birthDate), [birthDate]);

  const showCategoryChoice = eligibleCategories.length > 1;
  const autoCategory = eligibleCategories.length === 1 ? eligibleCategories[0] : null;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setError(null);
    if (!file) {
      setPaymentProof(null);
      return;
    }
    if (!ALLOWED_MIMETYPES.includes(file.type)) {
      setError('O comprovante deve ser PNG, JPG ou PDF.');
      setPaymentProof(null);
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError('Arquivo muito grande (máximo 5MB).');
      setPaymentProof(null);
      return;
    }
    setPaymentProof(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!name.trim() || !birthDate) {
      setError('Preencha nome e data de nascimento.');
      return;
    }
    if (!phone.trim()) {
      setError('Informe um telefone de contato.');
      return;
    }
    if (eligibleCategories.length === 0) {
      setError('Não foi possível determinar a categoria para esta data de nascimento.');
      return;
    }
    if (showCategoryChoice && !category) {
      setError('Escolha em qual categoria deseja se inscrever.');
      return;
    }
    if (!paymentProof) {
      setError('Anexe o comprovante de pix.');
      return;
    }

    const finalCategory = showCategoryChoice ? category : autoCategory;

    setSubmitting(true);
    try {
      const response = await registrationsService.create({
        name: name.trim(),
        birthDate,
        phone: phone.trim(),
        category: finalCategory,
        paymentProof,
      });
      if (response.success) {
        setSuccess({
          category: response.data.category,
          name: response.data.name,
        });
      }
    } catch (err) {
      setError(err.error?.message || 'Erro ao enviar inscrição. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: colors.primary }}>
        <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md text-center">
          <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
          <h2 className="text-2xl font-bold mb-2" style={{ color: colors.primary }}>
            Inscrição enviada!
          </h2>
          <p className="text-gray-700 mb-4">
            <strong>{success.name}</strong>, sua inscrição na categoria{' '}
            <strong className="capitalize">{success.category}</strong> foi recebida e está aguardando aprovação do organizador.
          </p>
          <p className="text-sm text-gray-500">
            Você será comunicado sobre a aprovação. Se precisar se inscrever em outra categoria
            (no caso de master/adulto), basta preencher o formulário novamente.
          </p>
          <button
            onClick={() => {
              setSuccess(null);
              setName('');
              setBirthDate('');
              setPhone('');
              setCategory('');
              setPaymentProof(null);
            }}
            className="mt-6 w-full px-6 py-2 text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: colors.primary }}
          >
            Fazer nova inscrição
          </button>
          <button
            onClick={() => navigate('/')}
            className="mt-3 w-full px-6 py-2 rounded-lg border hover:bg-gray-50"
            style={{ borderColor: colors.primary, color: colors.primary }}
          >
            Voltar à página principal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: colors.primary }}>
      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md mx-auto">
        <div className="text-center mb-6">
          <Shield className="mx-auto h-12 w-12 mb-3" style={{ color: colors.primary }} />
          <h1 className="text-2xl font-bold" style={{ color: colors.primary }}>
            Inscrição no Torneio
          </h1>
          <p className="text-sm text-gray-600 mt-2">Goleiro Raiz Academy</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do atleta
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data de nascimento
            </label>
            <input
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
              required
              max={new Date().toISOString().split('T')[0]}
            />
            {age !== null && age >= 0 && (
              <p className="text-xs text-gray-500 mt-1">Idade: {age} anos</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefone para contato
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(00) 00000-0000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
              required
            />
          </div>

          {birthDate && (
            <div>
              {showCategoryChoice ? (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoria que deseja disputar
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Você tem idade para competir em mais de uma categoria. Escolha qual quer disputar:
                  </p>
                  <div className="space-y-2">
                    {eligibleCategories.map((cat) => (
                      <label key={cat} className="flex items-center p-3 border rounded-md cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="category"
                          value={cat}
                          checked={category === cat}
                          onChange={(e) => setCategory(e.target.value)}
                          className="mr-3"
                        />
                        <span className="capitalize">{CATEGORY_LABELS[cat]}</span>
                      </label>
                    ))}
                  </div>
                </>
              ) : autoCategory ? (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-gray-700">
                    Você será inscrito na categoria:
                  </p>
                  <p className="font-semibold capitalize" style={{ color: colors.primary }}>
                    {CATEGORY_LABELS[autoCategory]}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-red-600">
                  Não foi possível determinar a categoria. Confira a data informada.
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comprovante de pix
            </label>
            <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              {paymentProof ? (
                <>
                  <CheckCircle className="w-6 h-6 text-green-500 mb-1" />
                  <p className="text-sm text-gray-700">{paymentProof.name}</p>
                  <p className="text-xs text-gray-500">{(paymentProof.size / 1024).toFixed(0)} KB</p>
                </>
              ) : (
                <>
                  <Upload className="w-6 h-6 text-gray-500 mb-1" />
                  <p className="text-sm text-gray-700">Clique para anexar</p>
                  <p className="text-xs text-gray-500">PNG, JPG ou PDF (até 5MB)</p>
                </>
              )}
              <input
                type="file"
                accept="image/png,image/jpeg,application/pdf"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 px-4 text-white rounded-md font-medium hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: colors.primary }}
          >
            {submitting ? 'Enviando...' : 'Enviar inscrição'}
          </button>
        </form>
      </div>
    </div>
  );
}
